import { Flex, Text } from '@chakra-ui/react';
import React from 'react';
import {
  FormFieldRadio,
} from '@publishing-engine/domain/types/article';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormFeedback, Radio } from '@morressier/alembic';
import { FieldLabel } from '../Field';
import { CustomFormFieldComponent } from '../FormField';

interface Props {
  fieldConfigs: (FormFieldRadio | null)[];
  title?: string;
  description?: string;
}

export const AssessmentFieldGroup: React.FC<Props> = props => {
  const {
    fieldConfigs,
    title,
    description,
  } = props;

  return !fieldConfigs.find(config => !!config) ? null : (
    <Flex flexDirection="column" gap={2}>
      {title && <Text textStyle="headingSection">{title}</Text>}
      <Text>{description}</Text>
      {fieldConfigs.map(config => {
        if (!config) return null;
        return <CustomFormFieldComponent key={config.name} config={config} />;
      })}
    </Flex>
  );
};

export const OptInFormGroup: React.FC<Props> = props => {
  const {
    fieldConfigs,
  } = props;

  return !fieldConfigs.find(config => !!config) ? null : (
    <RadioGroupFieldForm fieldConfigs={fieldConfigs} />
  );
};

const RadioGroupFieldForm: React.FC<Props> = (props) => {
  const { fieldConfigs } = props
  const { control } = useFormContext();


  return (
    fieldConfigs.map((field) => {
      const name = field?.caseDataFields?.main;
      const dependency = field?.params?.externalFieldDependency;
      const shouldRender = (() => {
        if (!dependency) return true;
        const value = useWatch({ name: dependency.path, control });
        return value === dependency.value;
      })();

      if (!shouldRender) return null;

      return (
        <RadioGroup
          key={name}
          name={name}
          labels={field?.params?.labels}
          options={field?.params?.validations?.options}
          isRequired={field?.params?.validations?.required || false}
        />
      );
    })

  );
};



export const RadioGroup = ({ name, labels, options, isRequired }: any) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const hasErrors = Object.keys(errors).length > 0

  return (
    <Flex flexDirection="column" gap={4} width="100%">
      <FieldLabel
        isRequired={isRequired}
        label={labels?.main}
        subText={labels?.subtitle}
      />

      <Controller
        name={name}
        control={control}
        rules={{
          required: isRequired ? `Please select an option for "${labels.main}"` : false,
        }}
        render={({ field }) => (
          <>
            {options?.map((opt: string) => (
              <Radio
                key={opt}
                name={field.name}
                value={opt}
                checked={field.value === opt}
                onChange={() => field.onChange(opt)}
                ariaLabel={opt}
                id={opt}
              >
                <Flex flexDirection="column">
                  <Text textStyle="headingItem">{labels?.options[opt]?.main}</Text>
                  {labels?.options[opt]?.secondary && (
                    <Text textStyle="textBody">{labels.options[opt].secondary}</Text>
                  )}
                </Flex>
              </Radio>
            ))}
          </>
        )}
      />
      {hasErrors && errors[name] ? (
        <FormFeedback variant="error">
          Select {labels.main} to proceed.
        </FormFeedback>
      ) : null}
    </Flex>
  );
};

